<template>
  <v-list-item :class="xs ? 'mx-0 px-0' : ''" @click.prevent="onActiveAction">
    <ConfirmationDialog
      :header-prop="$t('el.restart_title')"
      :text="$t('el.restart_text')"
      :open="isDialogOpen"
      @click="onResetTrainingConfirmed"
      @close="onResetTrainingDenied"
    ></ConfirmationDialog>
    <v-list-item-avatar class="align-self-start" :class="xs ? 'mx-0 px-0' : ''">
      <!-- <v-icon class="d-flex" v-if="progress.progressPct > 99" color="success">mdi-checkbox-marked-circle</v-icon> -->
      <v-icon class="d-flex" v-if="isFinished" color="success">mdi-checkbox-marked-circle</v-icon>
      <v-progress-circular v-else class="d-flex" :width="7" :size="20" :value="progress.progressPct" :color="progress.color"></v-progress-circular>
    </v-list-item-avatar>

    <v-list-item-content class="align-self-start">
      <v-list-item-title class="text-subtitle-1 font-weight-medium">
        {{ module.name }}
      </v-list-item-title>

      <!-- Subheader with status information -->
      <div class="caption font-weight-light d-flex flex-column">
        <div v-if="progress.answeredQuestionsCount === 0 && module.is_practice == 1" class="d-flex">
          {{ $t('el.trainingNotStartedHint', { questionsCount: moduleStatistic.questionsCount, timeInMin: Math.ceil(progress.questionsCount * 1.5) }) }}
        </div>

        <div v-else-if="module.is_practice == 1" class="d-flex">
          {{ progress.answeredQuestionsCount }} von {{ progress.questionsCount }} Fragen gelöst - davon {{ progress.correctOnes }} korrekt beantwortet.
        </div>

        <div v-else-if="module.is_practice == 0" class="d-flex flex-row align-center mb-2">
          <div v-if="!isFinished" class="d-flex mr-4">{{ progress.questionsCount }} Fragen</div>
          <div v-if="module.timeout > 0" class="d-flex info--text">
            <v-chip color="error lighten-2" style="font-weight: bold" x-small>{{ $t('el.timeout', { timeout: module.timeout }) }} </v-chip>
          </div>
        </div>
      </div>

      <!-- module description -->
      <div class="text-body-2 wrapping">{{ module.description }}</div>

      <!-- <div class="text-body-2 wrapping">{{ userState.status }}</div> -->

      <div>
        <!-- RESULTS - Show detail results of a completed training -->
        <v-btn v-if="isFinished && isTraining" elevation="0" rounded class="mt-2 btn" @click="goToResult()"
          >{{ $t('el.showResults') }}<v-icon small class="ml-1">mdi-chart-pie</v-icon>
        </v-btn>
      </div>
    </v-list-item-content>

    <v-list-item-action class="align-self-start">
      <div v-if="!xs" class="d-flex caption font-weight-light">{{ dateCreated }}</div>

      <!-- RESUME an already started training -->
      <v-btn
        v-if="(module.is_practice == 1 || (module.is_practice == 0 && module.timeout === 0)) && progress.answeredQuestionsCount > 0 && !isFinished"
        rounded
        elevation="0"
        :color="theme.appBar.other.bg"
        :class="`${theme.appBar.other.color}--text`"
        class="mt-2 btn"
        @click="onResume()"
        >{{ $t('el.resume') }}<v-icon>mdi-play</v-icon>
      </v-btn>

      <!-- Reset button for trainings -->
      <!-- RESTART -->
      <v-btn v-if="isTraining && (progress.answeredQuestionsCount > 0 || isFinished)" elevation="0" rounded class="mt-2 btn" @click.stop="onResetTraining"
        >{{ $t('el.restart') }}
        <v-icon>mdi-restart</v-icon>
      </v-btn>

      <!-- START a training or an exam -->
      <v-btn
        v-else-if="!isFinished"
        rounded
        elevation="0"
        :color="theme.appBar.other.bg"
        :class="`${theme.appBar.other.color}--text`"
        class="mt-2 btn"
        @click.stop="onStart"
      >
        <span v-if="!xs"> {{ $t('el.start') }} </span>
        <v-icon>mdi-play</v-icon>
      </v-btn>

      <v-list-item-icon></v-list-item-icon>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import { mapState, mapMutations } from 'vuex';
import constants from './constants';
import dateTimeMixin from '@/mixins/dateTimeMixin';
import configMixin from '@/mixins/configMixin';
import commonMixin from '@/mixins/common';
import elearningMixin from './ElearningMixin';
import ConfirmationDialog from '@/dialogs/ConfirmationDialog.vue';

export default {
  name: 'ELModuleItem',
  components: { ConfirmationDialog },
  mixins: [dateTimeMixin, commonMixin, configMixin, elearningMixin],
  props: {
    module: Object,
  },
  data: () => ({
    isDialogOpen: false,
  }),
  computed: {
    ...mapState('elearning', ['modules', 'userStateByModuleId', 'answers', 'questions', 'answersByQuestionId']),

    progress() {
      let moduleId = this.module.id;
      console.debug('Calculate progress for moduleId', moduleId);
      let userState = this.userStateByModuleId[moduleId];
      let moduleStats = this.getModuleStatistics(this.module);
      if (!userState) {
        console.debug('NO userState for module id=', moduleId);
      }
      let { correctOnes, answeredQuestionsCount } = moduleStats;
      let questionsCount = moduleStats.questionsCount;
      let response = {
        questionsCount,
        answeredQuestionsCount,
        correctOnes,
        progressPct: questionsCount === 0 ? 0 : (100 * answeredQuestionsCount) / questionsCount,
        correctInPct: questionsCount === 0 ? 0 : (100 * correctOnes) / questionsCount,
        status: userState.status,
      };

      switch (userState.status) {
        case constants.STATUS.starting:
          return { ...response, color: this.theme.elements.activeColor };
        case constants.STATUS.canceled:
          return { ...response, color: 'error' };
        case constants.STATUS.finished:
          return { ...response, color: 'success' };
      }
      return { ...response, color: 'success' };
    },
    isFinished() {
      return this.progress.status === constants.STATUS.finished;
      //return this.progress.answeredQuestionsCount === this.progress.questionsCount;
    },
    dateCreated() {
      return this.formatIsoDateStringAsLocaleShortDate(this.module.datecreated);
    },
    isExam() {
      return this.elmx_isExam(this.module);
    },
    isTraining() {
      return this.elmx_isTraining(this.module);
    },
    userState() {
      return this.elmx_getModuleUserState(this.module);
    },
    moduleStatistic() {
      return this.$store.getters['elearning/getModuleStatistics'](this.module);
    },
  },
  methods: {
    ...mapMutations('elearning', ['resetUserAnswer']),

    getModuleStatistics(module) {
      return this.$store.getters['elearning/getModuleStatistics'](module);
    },

    goTo(slideIndex) {
      this.elmx_goTo(this.module, slideIndex);
    },

    goToResult() {
      this.elmx_goToResult(this.module);
    },

    onResetTraining() {
      this.isDialogOpen = true;
    },
    onResetTrainingConfirmed() {
      let text = this.$t('el.trainingReset', { moduleName: this.module.name });
      let userState = this.elmx_getModuleUserState(this.module);
      userState.status = constants.STATUS.starting;
      this.elmx_createAndSendSendboxItem({
        module: this.module,
        userState,
        text,
        action: 'saveStateTransition',
        showAsUserInfo: true,
        showInSendbox: true,
        writeAuditLog: true,
      });
      this.resetUserAnswer({ moduleId: this.module.id });
      this.isDialogOpen = false;
    },
    onResetTrainingDenied() {
      this.isDialogOpen = false;
    },
    onActiveAction() {
      if (this.isTraining) {
        if (this.isFinished) {
          this.goToResult();
        } else if (this.progress.answeredQuestionsCount > 0 && !this.isFinished) {
          this.onResume();
        } else {
          this.onStart();
        }
      } else {
        if (this.module.timeout === 0 && this.progress.answeredQuestionsCount > 0 && !this.isFinished) {
          this.onResume();
        } else if (!this.isFinished) {
          this.onStart();
        }
      }
    },
    onResume() {
      console.debug('onResume', this.progress.answeredQuestionsCount);
      this.elmx_goTo(this.module, this.progress.answeredQuestionsCount);
    },

    onStart() {
      let module = this.module;

      let userState = this.elmx_getModuleUserState(module);
      if (this.isExam) {
        console.debug('start exam userState', JSON.stringify(userState, null, 2));
        if (this.isFinished) {
          this.$store.commit('elearning/setDialog', {
            id: 'examAlreadyPassed', // dialog id that is provided with callbacks and handled at the component that hosts the dialog, the ELearningHeader in this case
            isOpen: true, // true=open
            title: this.$t('el.examAlreadyCompletedTitle'), // dialog title
            text: this.$t('el.examAlreadyCompleted'), // dialog content
            okText: this.$t('label.yes'), //text for ok button

            params: { module: module },
          });
        } else {
          if (module.timeout === 0) {
            this.$store.commit('elearning/setDialog', {
              id: 'examStartConfirm', // dialog id that is provided with callbacks and handled at the component that hosts the dialog, the ELearningHeader in this case
              isOpen: true, // true=open
              title: this.$t('el.examStartTitle'),
              text: this.$t('el.examStartConfirm'),
              okText: this.$t('label.yes'),
              cancelText: this.$t('label.no'), //text for ok buttonText: this.$t('label.yes'), //text for ok button

              params: { module: module },
            });
          } else {
            this.$store.commit('elearning/setDialog', {
              id: 'examStartConfirm', // dialog id that is provided with callbacks and handled at the component that hosts the dialog, the ELearningHeader in this case
              isOpen: true, // true=open
              title: this.$t('el.examStartTitle'),
              text: this.$t('el.examStartConfirmWithTimeout', { timeoutInMin: module.timeout }),
              okText: this.$t('label.yes'),
              cancelText: this.$t('label.no'), //text for ok buttonText: this.$t('label.yes'), //text for ok button

              params: { module: module },
            });
          }
        }
      } else {
        userState.status = constants.STATUS.starting;
        this.elmx_createAndSendSendboxItem({
          module,
          userState,
          text: `${module.name} gestartet`,
          action: 'saveStateTransition',
          showAsUserInfo: true,
          showInSendbox: true,
          writeAuditLog: true,
        });
        this.elmx_goTo(module, 0);
      }
    },
  },
};
</script>
<style scoped>
.wrapping {
  white-space: pre-line;
}
.btn {
  width: 120px;
}
</style>
